import { useApolloClient } from '@vue/apollo-composable'
import type { ProductBase } from '~/types/productBase'
import queryRoute from '~/graphql/queries/queryRoute.graphql'
import type { AlgoliaFilterLabels } from '~/types/algoliaFilterLabels'

export default defineNuxtRouteMiddleware(async (to, from) => {
    const nuxtApp = useNuxtApp()
    if (process.client && nuxtApp.isHydrating && nuxtApp.payload.serverRendered) return

    if (process.client && to?.path === from?.path) return

    const pagePrefix = '_PAGE_PREFIX'
    const categoryRequestPending = useState('categoryRequestPending')
    const productRequestPending = useState('productRequestPending')
    const commonStockStatus = useState<boolean>('commonStockStatus')
    const algoliaFilterLabels = useState<AlgoliaFilterLabels[]>('algoliaFilterLabels')

    categoryRequestPending.value = true
    productRequestPending.value = true

    const { resolveClient } = useApolloClient()
    const client = resolveClient()
    const { SEARCH_PAGE_PREFIX, URL_AUTH_PREFIX, URL_CART, URL_CUSTOMER_WISHLIST, STORE_LOCATOR_PREFIX } =
        useAppConfig()
    const { $i18n } = useNuxtApp()

    // Fetch the application configuration.
    const appConfig = useAppConfig()

    // Extract values with the "_PAGE_PREFIX" suffix from the configuration.
    const valuesWithSuffix = Object.keys(appConfig)
        .filter((key) => key.endsWith(pagePrefix))
        .map((key) => appConfig[key])

    // Parse the URL path.
    const [, , urlPrefix] = to.fullPath.split('/')

    // Check if the `urlPrefix` value is present in `valuesWithSuffix` and if it exists.
    if (!urlPrefix || !valuesWithSuffix.includes(urlPrefix)) {
        return
    }

    try {
        if (
            to.path !== `/${$i18n.locale.value}` &&
            !to.path.startsWith(`/${$i18n.locale.value}/auth`) &&
            !to.path.startsWith(`/${$i18n.locale.value}/customer`) &&
            !to.path.startsWith(`/${$i18n.locale.value}/s`) &&
            !to.path.startsWith('/_')
        ) {
            const { data } = await client.query({
                query: queryRoute,
                variables: {
                    url: to.path.substring(3),
                },
            })
            if (data.route.redirectCode) {
                return navigateTo(`/${$i18n.locale.value}/${data.route.relativeUrl}`, {
                    redirectCode: data.route.redirectCode,
                    ...(data.route.type === 'CMS_PAGE' && { external: true }),
                })
            }

            const productData = useState<ProductBase>('productData')
            const categoryData = useState('categoryData')
            const productType = useState<string>('productType')

            if (data.route.item)
                switch (data.route.item.__typename) {
                    case 'Category':
                        categoryData.value = data.route.item
                        categoryRequestPending.value = false
                        break
                    case 'Product':
                    case 'ProductGrouped':
                    case 'ProductConfigurable':
                        productData.value = Product.set(data.route.item)
                            .mirakl()
                            .url()
                            .badges()
                            .image()
                            .breadcrumbs()
                            .dataLayer()
                            .brand().result
                        productRequestPending.value = false
                        productType.value = data.route.item.__typename
                        break
                }

            commonStockStatus.value = data.route.commonStockEnabled
            algoliaFilterLabels.value = data.route.algolia?.filters

            const validPaths = [
                SEARCH_PAGE_PREFIX,
                URL_AUTH_PREFIX,
                URL_CART,
                URL_CUSTOMER_WISHLIST,
                STORE_LOCATOR_PREFIX,
            ]
            const validPathsRegex = new RegExp(`^(/[a-z]{2})/(${validPaths.join('|').replace('/', '')})`)

            if (!validPathsRegex.test(to.path) && data.route.item === null) {
                throw createError({
                    statusCode: 404,
                })
            }
        }
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            console.error('Error in route global middleware:', error, to.path)
            console.error('Error in route global path:', to.path)
            console.error(
                'Error in route global middleware - data:',
                error?.clientErrors,
                error?.graphQLErrors,
                error?.networkError,
                error?.protocolErrors,
            )
        }
        throw createError({
            statusCode: 404,
        })
    }
})
